import React, { Component } from "react";
import styled from "styled-components";
import { component } from "../../component";
import {
  desktopBreakpoint,
  mobileBreakpoint,
  innerWidth,
} from "../../styleBits";
import { ImageEnum, imgBlob } from "../../cssImg";
import { state } from "../../state";
import { DropdownButton } from "../DropdownButton";
import PageLink from "../PageLink";

const w1024 = `@media (max-width: 1024px)`;

const brandBlueForTheme = (theme: string) =>
  theme === "dark" ? "#FFFFFF" : "#322387";

@component
class NavBar extends Component {
  static styles = styled.header`
    height: 80px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    position: relative;
    margin-left: -24px;
    margin-right: -24px;

    ${w1024} {
      margin: 0 16px;
    }

    #logo {
      display: flex;
      border: 2px solid transparent;
      padding: 4px;

      &:hover,
      &:focus {
        outline: 2px solid var(--outline-border);
        background: var(--outline-focused);
        border-radius: 16px;
      }
    }

    .donate-and-socials {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .socials {
      display: flex;

      ${desktopBreakpoint} {
        margin-left: auto;
        margin-right: 20px;
      }

      ${mobileBreakpoint} {
        display: none;
      }

      @media (max-width: 1024px) {
        display: none;
      }

      > * {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 24px;
        height: 24px;
        padding: 12px;

        &:hover,
        &:focus {
          outline: 2px solid var(--outline-border);
          background-color: var(--outline-focused);
          border-radius: 50%;
        }

        img {
          filter: invert(11%) sepia(48%) saturate(7280%) hue-rotate(250deg)
            brightness(79%) contrast(91%);

          ${({ theme }) =>
            theme === "dark" &&
            `
              filter: invert(1);
            `}
        }
      }
    }

    .cont {
      ${desktopBreakpoint} {
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      ${mobileBreakpoint} {
        position: fixed;
        left: 0;
        right: 0;
        top: 80px;
        height: calc(100vh - 80px);
        background: var(--mobile-menu-bg);
        z-index: 1;
        overflow: auto;
        display: flex;
        flex-direction: column;
        padding: 0 16px;

        > * {
          flex: 0 0 auto;
        }
      }
    }

    .theme-selector {
      ${desktopBreakpoint} {
        &.mobile {
          display: none;
        }
      }

      ${mobileBreakpoint} {
        &.desktop {
          display: none;
        }
        margin-bottom: 8px;
        margin-top: 8px;
      }
    }

    ul {
      list-style-type: none;
      margin: 0;
      padding: 0;
      ${desktopBreakpoint} {
        display: flex;
        align-items: center;
      }
      a {
        color: var(--header-links);
        padding: 12px 16px;
        font-weight: 800;
        font-size: 16px;
        line-height: 16px;

        &:hover,
        &:focus {
          outline: 2px solid var(--outline-border);
          background: var(--outline-focused);
          border-radius: 24px;
        }

        @media (max-width: 830px) {
          padding: 0px 8px;
        }
      }

      ${mobileBreakpoint} {
        a {
          display: block;
          height: 70px;
          line-height: 70px;
          text-align: center;
        }
      }
    }

    a {
      text-decoration: none;
    }

    &:not(.showMenu) {
      .cont {
        ${mobileBreakpoint} {
          display: none;
        }
      }
    }

    .menu {
      position: absolute;
      right: 0;
      top: 0;
      bottom: 0;
      margin: auto;
      width: 40px;
      height: 40px;
      border: none;
      background: transparent;
      display: flex;
      justify-content: center;
      align-items: center;

      &:focus {
        outline: 2px solid var(--outline-border);
        border-radius: 50%;
        background: var(--outline-focused);
      }
    }

    ${desktopBreakpoint} {
      .menu {
        display: none;
      }
    }
  `;

  onEscPress = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      if (state.showMenu) {
        const menu: HTMLElement = document.querySelector(".menu");
        menu?.focus();
      }
      state.showMenu = false;
    }
  };

  componentDidMount(): void {
    window.addEventListener("keydown", this.onEscPress);
  }

  componentWillUnmount(): void {
    window.removeEventListener("keydown", this.onEscPress);
  }

  componentDidUpdate(
    prevProps: Readonly<{}>,
    prevState: Readonly<{}>,
    snapshot?: any
  ): void {
    const main = document.querySelector("main");
    const footer = document.querySelector("footer");
    if (state.showMenu) {
      main.setAttribute("inert", "");
      main.setAttribute("aria-hidden", "true");

      footer.setAttribute("inert", "");
      footer.setAttribute("aria-hidden", "true");
    } else {
      main.removeAttribute("inert");
      main.removeAttribute("aria-hidden");

      footer.removeAttribute("inert");
      footer.removeAttribute("aria-hidden");
    }
  }

  render(Container?) {
    return (
      <Container className={state.showMenu && "showMenu"} theme={state.theme}>
        <PageLink to="/" id="logo">
          <img
            className="logo"
            width={114}
            height={40}
            src={imgBlob(ImageEnum.logo, { black: brandBlueForTheme(state.theme) })}
            alt="SignUp logo, opens home page"
          />
        </PageLink>
        <button
          aria-label="Open menu"
          className="menu"
          onClick={() => {
            state.showMenu = !state.showMenu;
            document.querySelector("html").scrollTo({
              behavior: "smooth",
              top: 0,
              left: 0,
            });
          }}
        >
          <img
            src={imgBlob(ImageEnum.menu, { black: brandBlueForTheme(state.theme) })}
            alt=""
            role="presentation"
          />
        </button>
        <nav className="cont" aria-label="Primary links">
          <DropdownButton
            className="theme-selector mobile"
            options={[
              {
                label: "Light mode",
                value: "light",
              },
              {
                label: "Dark mode (High contrast)",
                value: "dark",
              },
              {
                label: "Light mode (High contrast)",
                value: "high-contrast",
              },
            ]}
            text="Theme"
            value={state.theme}
            onChange={(value) => {
              if (value) {
                document.querySelector("body").classList.remove("light");
                document.querySelector("body").classList.remove("dark");
                document
                  .querySelector("body")
                  .classList.remove("high-contrast");
                document.querySelector("body").classList.add(value);
                localStorage.setItem("theme", value);
                state.theme = value;
                gtag("event", `Theme changed to ${value}`);
              }
            }}
          />

          <ul>
            <li>
              <PageLink to="/#movies" onClick={() => (state.showMenu = false)}>
                Movies
              </PageLink>
            </li>
            <li>
              <PageLink to="/#faqs" onClick={() => (state.showMenu = false)}>
                FAQs
              </PageLink>
            </li>
            {/* <li><a href="#our-team" onClick={() => state.showMenu = false}>Our Team</a></li> */}
            <li>
              <PageLink to="/#press" onClick={() => (state.showMenu = false)}>
                Press
              </PageLink>
            </li>
            <li>
              <PageLink to="/#contact-us" onClick={() => (state.showMenu = false)}>
                Contact Us
              </PageLink>
            </li>
            <li>
              <PageLink to="/#work-with-us" onClick={() => (state.showMenu = false)}>
                Work With Us
              </PageLink>
            </li>
            <li>
              <PageLink to="/stories" onClick={() => (state.showMenu = false)}>
                Stories
              </PageLink>
            </li>
            {state.showMenu ? (
              <li className="sr-only">
                <button
                  onClick={() => {
                    state.showMenu = false;
                    const menu: HTMLElement = document.querySelector(".menu");
                    menu?.focus();
                  }}
                >
                  Close Menu
                </button>
              </li>
            ) : null}
          </ul>
        </nav>
        <div className="donate-and-socials">
          <div className="socials">
            <a
              aria-label="Follow us on Instagram; opens in a new tab"
              href="https://www.instagram.com/signupcaptions/"
              target="_blank"
              rel="noopener noreferrer"
              className="instagram"
            >
              <img src={imgBlob(ImageEnum.socials_instagram)} alt="" />
              <span role="presentation" className="hidden">
                Instagram
              </span>
            </a>
            <a
              aria-label="Follow us on Facebook; opens in a new tab"
              href="https://www.facebook.com/signupcaptions"
              target="_blank"
              rel="noopener noreferrer"
              className="facebook"
            >
              <img src={imgBlob(ImageEnum.socials_facebook)} alt="" />
              <span role="presentation" className="hidden">
                Facebook
              </span>
            </a>
            <a
              aria-label="Follow us on Linkedin; opens in a new tab"
              href="https://www.linkedin.com/company/signup-captions/"
              target="_blank"
              rel="noopener noreferrer"
              className="linkedin"
            >
              <img src={imgBlob(ImageEnum.socials_linkedIn)} alt="" />
              <span role="presentation" className="hidden">
                Linkedin
              </span>
            </a>
          </div>
          <DropdownButton
            className="theme-selector desktop"
            options={[
              {
                label: "Light mode",
                value: "light",
              },
              {
                label: "Dark mode (High contrast)",
                value: "dark",
              },
              {
                label: "Light mode (High contrast)",
                value: "high-contrast",
              },
            ]}
            text="Theme"
            value={state.theme}
            onChange={(value) => {
              if (value) {
                document.querySelector("body").classList.remove("light");
                document.querySelector("body").classList.remove("dark");
                document
                  .querySelector("body")
                  .classList.remove("high-contrast");
                document.querySelector("body").classList.add(value);
                localStorage.setItem("theme", value);
                state.theme = value;
                gtag("event", `Theme changed to ${value}`);
              }
            }}
          />
        </div>
      </Container>
    );
  }
}

@component
export class Header extends Component {
  static styles = styled.div`
    position: sticky;
    top: 0;

    background: var(--surface-000);
    color: white;
    z-index: 3;
    /* Held still during route transitions; see App.css. */
    view-transition-name: site-header;

    .innerCont {
      margin: 0 auto;
      ${desktopBreakpoint} {
        max-width: ${innerWidth};
      }
    }
  `;

  render() {
    return (
      <div className="innerCont">
        <NavBar />
      </div>
    );
  }
}
const Shadow = styled.div`
  position: fixed;
  view-transition-name: site-header-shadow;
  box-shadow: 0 6px 4px rgba(0, 0, 0, 0.25);
  width: 100%;
  height: 10px;
  top: 70px;
`;
const HideShadow = styled.div`
  position: relative;
  .hideIt {
    position: relative;
    top: -10px;
    height: 10px;
    background-color: var(--surface-000);
  }
`;

export function HeaderShadow() {
  return (
    <>
      <Shadow />
      <HideShadow>
        <div className="hideIt" />
      </HideShadow>
    </>
  );
}
